import { Component, ViewChild, ElementRef, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { FormatService } from '../../format.service';
import { InvestmentChartService } from '../../investment-chart.service';
import { validateRange, validateRangeAllowZero } from '../../range-validate';
import {
  StampDutyKarnatakaCalculatorService, StampDutyResult, KarnatakaAreaType, STAMP_DUTY_CONSTRAINTS
} from './stamp-duty-karnataka-calculator.service';

@Component({
  selector: 'app-stamp-duty-karnataka-calculator',
  standalone: true,
  imports: [FormsModule, RouterLink],
  providers: [InvestmentChartService],
  templateUrl: './stamp-duty-karnataka-calculator.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./stamp-duty-karnataka-calculator.scss']
})
export class StampDutyKarnatakaCalculator implements OnDestroy {
  fmt = inject(FormatService);
  private calc = inject(StampDutyKarnatakaCalculatorService);
  private charts = inject(InvestmentChartService);
  private cdr = inject(ChangeDetectorRef);


  @ViewChild('breakdownRef') breakdownRef!: ElementRef<HTMLCanvasElement>;

  readonly C = STAMP_DUTY_CONSTRAINTS;

  saleConsideration = 50_00_000;
  guidanceValue = 0;
  areaType:          KarnatakaAreaType = 'urban';

  formErrors:    Record<string, string>  = {};
  touched:       Record<string, boolean> = {};
  showFormErrors = false;

  calculated = false;
  result:     StampDutyResult | null = null;

  private validateForm(): boolean {
    const errors: Record<string, string> = {};
    const e1 = validateRange(this.saleConsideration, this.C.saleValue.min, this.C.saleValue.max, 'Sale consideration value');
    if (e1) errors['saleConsideration'] = e1;
    const e2 = validateRangeAllowZero(this.guidanceValue, this.C.guidanceValue.min, this.C.guidanceValue.max, 'Guidance value');
    if (e2) errors['guidanceValue'] = e2;
    this.formErrors = errors;
    return Object.keys(errors).length === 0;
  }

  get isFormValid(): boolean { return this.validateForm(); }

  setAreaType(type: KarnatakaAreaType): void {
    this.areaType = type;
    if (this.calculated) {
      this.calculated = false;
      this.charts.destroyAll();
    }
  }

  onFieldChange(field: string): void {
    this.touched[field] = true;
    this.validateForm();
    if (this.calculated) {
      this.calculated = false;
      this.charts.destroyAll();
    }
  }

  onFieldBlur(field: string): void {
    this.touched[field] = true;
    this.validateForm();
  }

  fieldError(field: string): string | null {
    return (this.touched[field] || this.showFormErrors) && this.formErrors[field]
      ? this.formErrors[field]
      : null;
  }

  hasError(field: string): boolean { return !!this.fieldError(field); }

  calculate(): void {
    this.showFormErrors = true;
    this.touched = { saleConsideration: true, guidanceValue: true };
    if (!this.validateForm()) return;

    this.result = this.calc.calculate(this.saleConsideration, this.guidanceValue, this.areaType);
    this.calculated = true;
    this.cdr.detectChanges();

    setTimeout(() => {
      const r = this.result!;
      this.charts.renderBreakdownBar(this.breakdownRef.nativeElement, [
        { label: 'Stamp Duty',       value: r.baseStampDuty,                  color: '#3b82f6' },
        { label: 'Cess & Surcharge', value: r.cessAmount + r.surchargeAmount, color: '#f59e0b' },
        { label: 'Registration Fee', value: r.registrationFee,                color: '#10b981' }
      ]);
    }, 60);
  }

  reset(): void {
    this.saleConsideration = 50_00_000;
    this.guidanceValue     = 0;
    this.areaType           = 'urban';
    this.formErrors         = {};
    this.touched            = {};
    this.showFormErrors     = false;
    this.calculated         = false;
    this.result             = null;
    this.charts.destroyAll();
  }

  ngOnDestroy(): void {
    this.charts.destroyAll();
  }
}
